import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { SicotService } from '../sicot.service';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { NotificheService } from './notifiche.service';
import { Notifica } from '../types/Notifica';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ItAlertComponent, ItIconComponent } from 'design-angular-kit';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatCard, MatCardHeader, MatCardTitle, MatCardActions, MatCardContent } from "@angular/material/card";
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

function normalizeNotifica(n: Notifica): Notifica {
  const dataOra = (n as unknown as { dataOra?: Date | string }).dataOra ?? n.data;
  const messaggio = (n as unknown as { messaggio?: string }).messaggio ?? n.descrizione ?? '';
  return { ...n, dataOra, messaggio };
}

@Component({
  selector: 'app-notifiche',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatButtonModule,
    CommonModule,
    MatDatepickerModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatIconModule,
    FooterComponent,
    HeaderComponent,
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    ItAlertComponent,
    ItIconComponent,
    RouterLink,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardActions,
    MatCardContent,
    MatTooltipModule,
    MatProgressBarModule
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' }
  ],
  templateUrl: './notifiche.component.html',
  styleUrl: './notifiche.component.css'
})
export class NotificheComponent implements AfterViewInit {

  isLoading = false;

  tabIndex = 0;
  formFiltri: FormGroup;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumnsNonLette: string[] = ['dataOra', 'messaggio', 'action', 'apri'];
  displayedColumnsLette: string[] = ['dataOra', 'messaggio', 'apri'];
  dataSource: MatTableDataSource<Notifica>;

  constructor(
    private siCotService: SicotService,
    private notificheService: NotificheService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.dataSource = new MatTableDataSource<Notifica>();
    this.formFiltri = this.fb.group({
      dataDa: [null as Date | null],
      dataA: [null as Date | null],
      cerca: ['']
    });
    this.dataSource.filterPredicate = (row: Notifica) => this.filterRow(row);
  }

  private filterRow(row: Notifica): boolean {
    const dataOra = row.dataOra ?? row.data;
    const messaggio = (row.messaggio ?? row.descrizione ?? '').toLowerCase();
    const dataDaRaw = this.formFiltri.get('dataDa')?.value;
    const dataARaw = this.formFiltri.get('dataA')?.value;
    const dataDa = dataDaRaw ? (typeof dataDaRaw === 'string' ? new Date(dataDaRaw) : dataDaRaw) : null;
    const dataA = dataARaw ? (typeof dataARaw === 'string' ? new Date(dataARaw) : dataARaw) : null;
    const cerca = (this.formFiltri.get('cerca')?.value ?? '').trim().toLowerCase();
    if (dataDa && dataOra) {
      const d = typeof dataOra === 'string' ? new Date(dataOra) : dataOra;
      if (d < dataDa) return false;
    }
    if (dataA && dataOra) {
      const d = typeof dataOra === 'string' ? new Date(dataOra) : dataOra;
      const end = new Date(dataA);
      end.setHours(23, 59, 59, 999);
      if (d > end) return false;
    }
    if (cerca && !messaggio.includes(cerca)) return false;
    return true;
  }

  ngOnInit(): void {
    this.fetchNotificheNonLette();
  }

  applyFilter(): void {
    this.dataSource.filter = 'x';
    this.dataSource.paginator = this.paginator;
  }

  resetFiltri(): void {
    this.formFiltri.reset({ dataDa: null, dataA: null, cerca: '' });
    this.applyFilter();
  }

  getDisplayDataOra(n: Notifica): Date | string | undefined {
    return n.dataOra ?? n.data;
  }

  getDisplayMessaggio(n: Notifica): string {
    return n.messaggio ?? n.descrizione ?? '';
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  private setData(data: Notifica[]): void {
    const normalized = data.map(normalizeNotifica);
    this.dataSource.data = normalized;
    this.dataSource.paginator = this.paginator;
    this.applyFilter();
  }

  fetchNotifiche(): void {
    this.siCotService.getNotifiche().subscribe({
      next: (data) => this.setData(data),
      error: (err) => console.error('Error fetching notifiche:', err)
    });
  }

  fetchNotificheLette(): void {
    this.isLoading = true; // Attiva lo spinner
    this.notificheService.getNotificheLette().subscribe({
      next: (data) => {
        this.setData(data);
        this.isLoading = false
      },
      error: (err) => console.error('Error fetching notifiche lette:', err)
    });
  }

  fetchNotificheNonLette(): void {
    this.isLoading = true; // Attiva lo spinner
    this.notificheService.getNotificheNonLette().subscribe({
      next: (data) => {
        this.setData(data);
        this.isLoading = false;
      },
      error: (err) => console.error('Error fetching notifiche non lette:', err)
    });
  }

  leggi(element: Notifica): void {
    this.notificheService.leggiNotifica(element.id!.toString()).subscribe({
      next: () => {
        this.dataSource.data = this.dataSource.data.filter((item) => item.id !== element.id);
      },
      error: (err) => console.error('Error lettura notifica:', err)
    });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.tabIndex = event.index;
    if (this.tabIndex === 1) this.fetchNotificheLette();
    else this.fetchNotificheNonLette();
  }

  apriRichiesta(notifica: Notifica): void {
    console.log(notifica.richiestaId)
    if (notifica.richiestaId) {
      this.router.navigate([`/richiesta/${notifica.richiestaId}`]);
    }
  }
}